import {ClassTransformer} from 'class-transformer';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BaseService} from '../base-service';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {HttpUtilityService} from '@shared/services/api/http-utility-service';
import {User} from '@app/layout/admin/services/api/user-interfaces';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {ConfigService} from '@shared/services/config/config.service';
import {actionStatusesById, alertStatusesById, AlertTicket, AlertTicketSearchParams, AttachmentUploadUrl, BulkTicketAggregationParams, EscalatedTicket, EscalatedTicketCreateBody, EscalatedTicketSearchParams, EscalatedTicketUpdateBody, Ticket, TICKET_LINK_TYPE_ALERT_TO_ACTION, TicketAggregationParams, TicketAPIResultFull, TicketAPIResultPartial, TicketAttachment, ticketBenefitsById, TicketBody, TicketChangeHistory, TicketChangeHistoryField, TicketComment, TicketCommentCreationFields, TicketCommentEditFields, TicketCommentResult, TicketEmail, TicketEmailContentType, TicketFieldId, TicketLink, TicketLinkDirection, TicketPriorityId, TicketReferenceData, TicketStatusId, TicketType} from '@shared/services/api/tickets/tickets.interface';
import {I18nMessage} from '@shared/api/model/i18n';
import moment, {Duration} from 'moment-timezone';
import {Task} from '@shared/services/api/task.interface';
import {Params} from '@angular/router';
import {SiteInfo} from '@shared/services/api/access-sites.interface';
import {ChartService} from '@app/layout/charts/chart.service';
import {TicketsStore} from '@shared/services/api/tickets/store/ticket-store';
import {AlertTicketFetchOptions, EscalatedTicketFetchOptions, SortOrder, TicketFetchResult, TicketPagedModel, TicketsApiClient} from '@shared/services/api/tickets/tickets-api-client';
import {HttpClient} from '@angular/common/http';
import Fields from 'gql-query-builder/build/Fields';
import {SiteService} from '@shared/services/api/site.service';
import {Equipment} from '@app/graphql-typings/graphql';
import {UserProfileService} from '@shared/services/api/user-profile.service';
import {ScheduleUtil} from '@app/layout/charts/schedule-util';
import {MessageTranslateService} from '@shared/services';
import {TicketUtils} from '@app/layout/sites/tickets/ticket-utils';

export const emailTrackingCampaignQueryParamName = 'utm_campaign';

export enum EmailTrackingCampaignName {
  APPROVE = 'approve-email',
  FORWARD = 'forward-email',
  ASSIGN = 'assign-email'
}


@Injectable({
  providedIn: 'root'
})
export class TicketsService extends BaseService {

  public static readonly TICKET_AGGREGATION_REFERENCES_KEYS = {
    USERS: 'users'
  };

  // in ms
  private readonly DISPLAY_LAST_FAULT_TRANSITIONED_AT_DIFF_THRESHOLD = 15 * 60 * 1000;

  private readonly ALERT_CHART_LINK_BACK_TRACK_DAYS = 7;
  private readonly ALERT_CHART_LINK_FORWARD_TRACK_DAYS = 7;

  private readonly COMMON_CHANGE_HISTORY_FIELDS_NOT_TO_DISPLAY = new Set([
      TicketFieldId.METADATA_CODE,
      TicketFieldId.UPDATED_AT,
      TicketFieldId.MANUAL_SAVINGS]);
  private readonly CHANGE_HISTORY_FIELDS_NOT_TO_DISPLAY_BY_TICKET_TYPE = new Map([
    [TicketType.ALERT, new Set(this.COMMON_CHANGE_HISTORY_FIELDS_NOT_TO_DISPLAY)],
    [TicketType.ESCALATED, new Set([TicketFieldId.INBOX, ...this.COMMON_CHANGE_HISTORY_FIELDS_NOT_TO_DISPLAY])]
  ]);

  private readonly colorByPriority: Map<number, string> = new Map<number, string>([
    [TicketPriorityId.PRIORITY_P1_CRITICAL, 'danger'],
    [TicketPriorityId.PRIORITY_P2_URGENT, 'danger'],
  ]);

  private readonly DEFAULT_COLOR = 'secondary';
  private readonly ticketsApiClient: TicketsApiClient;
  private readonly ticketsStore: TicketsStore;
  private readonly logger: AceLogger;

  constructor(private translateService: MessageTranslateService,
              private configService: ConfigService,
              private chartService: ChartService,
              private userProfileService: UserProfileService,
              siteService: SiteService,
              http: HttpClient,
              httpUtilityService: HttpUtilityService,
              classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(TicketsService.name);
    this.ticketsApiClient = new TicketsApiClient(http, classTransformer, siteService, httpUtilityService, logService);
    this.ticketsStore = new TicketsStore(logService);
  }

  public getAssigneesForSite(siteId: number): Observable<User[]> {
    return this.ticketsApiClient.getAssigneesForSites(siteId != null ? [siteId] : []).pipe(map(usersBySiteId => usersBySiteId?.get(siteId) || []));
  }

  public getAssigneesForSites(siteIds: number[]): Observable<Map<number, User[]>> {
    return this.ticketsApiClient.getAssigneesForSites(siteIds);
  }

  public getTicketsById(ticketIds: string[], limit: number, offset: number): Observable<Ticket[]> {
    return this.ticketsApiClient.getTicketsById(ticketIds, limit, offset)
        .pipe(map(tickets =>
            tickets.map(ticket => this.registerGraphQLTicketInStore(<Ticket>ticket))));
  }

  public getTicketById(ticketId: string): Observable<Ticket> {
    return this.getTicketsById([ticketId], 1, 0).pipe(map(tickets => {
      return tickets && tickets.length ? tickets[0] : null;
    }));
  }

  // GraphQL tickets can be nested, this method recursively register all tickets from root all the way to leaves.
  private registerGraphQLTicketInStore<T extends Ticket>(ticket: T, ticketDepth: number = 0, ticketMaxDepth: number = 1): T | null {
    const atMaxDepth = ticketDepth === ticketMaxDepth;
    const registeredTicket = atMaxDepth ? <T>this.ticketsStore.getTicketById(ticket.ticket_id) : this.ticketsStore.addTicket(ticket);
    if (!atMaxDepth) {
      registeredTicket?.ticket_links?.forEach(link => {
        link.ticket = this.registerGraphQLTicketInStore(link.ticket, ticketDepth + 1, ticketMaxDepth);
      });
    }
    return registeredTicket;
  }

  private handleTicketFetchResult<T extends Ticket>(result: TicketFetchResult<T>): TicketPagedModel<T[]> {
    return {
      ...result.pagedTickets,
      data: result?.pagedTickets?.data.map(ticket => this.registerGraphQLTicketInStore(ticket, 0, result.ticketLinksDepth))
    };
  }

  public getEscalatedTicketsWithFields(fields: Fields,
                                       searchParams: EscalatedTicketSearchParams,
                                       limit: number,
                                       offset: number,
                                       orderBy?: string[]): Observable<TicketPagedModel<EscalatedTicket[]>> {
    return this.ticketsApiClient.getEscalatedTicketsWithFields(fields, searchParams, limit, offset, orderBy)
        .pipe(map(fetchResult => this.handleTicketFetchResult(fetchResult)));
  }

  public getEscalatedTickets(searchParams: EscalatedTicketSearchParams,
                             limit: number,
                             offset: number,
                             orderBy?: string[],
                             fetchOptions?: EscalatedTicketFetchOptions): Observable<TicketPagedModel<EscalatedTicket[]>> {
    return this.ticketsApiClient.getEscalatedTickets(searchParams, limit, offset, orderBy, fetchOptions)
        .pipe(map(fetchResult => this.handleTicketFetchResult(fetchResult)));
  }

  public getAlertTickets(searchParams: AlertTicketSearchParams,
                         limit: number,
                         offset: number,
                         orderBy?: string[],
                         fetchOptions?: AlertTicketFetchOptions): Observable<TicketPagedModel<AlertTicket[]>> {
    return this.ticketsApiClient.getAlertTickets(searchParams, limit, offset, orderBy, fetchOptions)
        .pipe(map(fetchResult => this.handleTicketFetchResult(fetchResult)));
  }

  public getAlertTicketById(ticketId: string, fetchOptions?: AlertTicketFetchOptions): Observable<AlertTicket> {
    return this.ticketsApiClient.getAlertTickets({ticket_id: ticketId, ticket_archived: null}, 1, 0, null, fetchOptions)
        .pipe(map((fetchResult) => this.handleFetchTicketByIdResult(fetchResult)));
  }

  public getEscalatedTicketById(ticketId: string, fetchOptions?: EscalatedTicketFetchOptions): Observable<EscalatedTicket> {
    return this.ticketsApiClient.getEscalatedTickets({ ticket_id: ticketId, ticket_archived: null }, 1, 0, null, fetchOptions)
        .pipe(map((fetchResult) => this.handleFetchTicketByIdResult(fetchResult)));
  }

  public getCommentsForEscalatedTicket(ticketId: string, offset: number, limit: number): Observable<{ count: number, comments: TicketComment[] }> {
    return this.ticketsApiClient.getCommentsForEscalatedTicket(ticketId, offset, limit);
  }

  public getAttachmentsForTicket(ticketId: string): Observable<TicketAttachment[]> {
    return this.ticketsApiClient.getAttachmentsForTicket(ticketId);
  }

  private handleFetchTicketByIdResult<T extends Ticket>(fetchResult: TicketFetchResult<T>): T | null {
    const ticket = fetchResult.pagedTickets && CollectionUtils.only(fetchResult.pagedTickets.data);
    return ticket ? this.registerGraphQLTicketInStore(ticket, 0, fetchResult.ticketLinksDepth) : null;
  }

  // region // ticket REST create/put/patch/delete
  private expandTicketLinksForTicket(ticket: Partial<Ticket>) {
    const ticketLinks: TicketLink[] = [];
    let ticketLinksUpdated = false;
    if ('source_ticket_ids' in ticket) {
      ticketLinksUpdated = true;
      ticket.source_ticket_ids?.forEach(sourceTicketId => {
        const sourceTicket = this.ticketsStore.getTicketById(sourceTicketId);
        ticketLinks.push({
          ticket_link_type: TICKET_LINK_TYPE_ALERT_TO_ACTION,
          direction: TicketLinkDirection.TO_SOURCE,
          ticket: sourceTicket,
        });
      });
    }
    // singular form only for now
    if ('target_ticket_id' in ticket) {
      ticketLinksUpdated = true;
      if (ticket.target_ticket_id) {
        const targetTicket = this.ticketsStore.getTicketById(ticket.target_ticket_id);
        ticketLinks.push({
          ticket_link_type: TICKET_LINK_TYPE_ALERT_TO_ACTION,
          direction: TicketLinkDirection.TO_TARGET,
          ticket: targetTicket,
        });
      }
    }

    if (ticketLinksUpdated) {
      ticket.ticket_links = ticketLinks;
    }
  }

  private expandTicketProperties<T extends Ticket>(ticket: Partial<Ticket>, { site, assigneesById, prioritiesById }: TicketReferenceData): T {
    const result: any = { ...ticket };

    if ('site_id' in ticket) {
      if (site == null || site.site_id !== ticket.site_id) {
        throw new Error('Failed while setting ticket site details, as site info with id ' + ticket.site_id + ' is missing from reference data.');
      }
      result.site = site;
    }
    if ('status_id' in ticket) {
      let statusMap;
      if (ticket.type === TicketType.ESCALATED) {
        statusMap = actionStatusesById;
      } else if (ticket.type === TicketType.ALERT) {
        statusMap = alertStatusesById;
      } else {
        throw new Error('Failed while setting ticket status, as ticket type ' + ticket.type + ' is not supported.');
      }
      result.status = statusMap.get(ticket.status_id);
    }
    if ('priority_id' in ticket) {
      if (prioritiesById == null) {
        throw new Error('Failed while setting ticket priority, as prioritiesById is missing from reference data.');
      }
      result.priority = prioritiesById.get(ticket.priority_id);
    }
    if ('benefit_id' in ticket) {
      const benefit = ticketBenefitsById.get(ticket.benefit_id);
      result.benefit = benefit ? [benefit] : [];
    }
    if ('assignee_ids' in ticket) {
      if (assigneesById == null) {
        throw new Error('Failed while setting ticket assignee details, as assigneesById is missing from reference data.');
      }
      const assigneeIds = ticket.assignee_ids || [];
      result.assignees = assigneeIds.map(aid => assigneesById.get(aid));
    }

    this.expandTicketLinksForTicket(result);

    return result;
  }

  private handleTicketApiResultFull<T extends Ticket>(apiResult: TicketAPIResultFull<T>,
                                                      references: TicketReferenceData): TicketAPIResultFull<T> {
    const { ticket, affected_tickets } = apiResult;

    const transformedTicket = ticket
        ? <T>this.ticketsStore.addTicket(this.expandTicketProperties(ticket, references))
        : null;
    const affectedTickets = affected_tickets?.map(affectedTicket =>
        this.ticketsStore.updateTicket(affectedTicket.ticket_id, this.expandTicketProperties(affectedTicket, references)));

    return {
      ticket: transformedTicket,
      affected_tickets: affectedTickets
    };
  }

  private handleTicketApiResultPartial<T extends Ticket>(requestTicket: T,
                                                         apiResult: TicketAPIResultPartial<T>,
                                                         references: TicketReferenceData): TicketAPIResultFull<T> {
    const { ticket, affected_tickets } = apiResult;

    const transformedTicket = ticket
        ? <T>this.ticketsStore.updateTicket(
            requestTicket.ticket_id,
            this.expandTicketProperties({
              ticket_id: requestTicket.ticket_id,
              type: requestTicket.type,
              ...ticket
            }, references))
        : null;

    const affectedTickets = affected_tickets?.map(affectedTicket =>
        this.ticketsStore.updateTicket(affectedTicket.ticket_id, this.expandTicketProperties(affectedTicket, references)));

    return {
      ticket: transformedTicket,
      affected_tickets: affectedTickets
    };
  }

  public createEscalatedTicket(ticket: EscalatedTicketCreateBody, references: TicketReferenceData): Observable<TicketAPIResultFull<EscalatedTicket>> {
    return this.ticketsApiClient.createEscalatedTicket(ticket)
        .pipe(
          map(apiResult => {
            if (!apiResult.ticket.comments && !apiResult.ticket.comment_count) {
              apiResult.ticket.comments = [];
              apiResult.ticket.comment_count = 0;
            }
            return apiResult;
          }),
          map(apiResult => this.handleTicketApiResultFull(apiResult, references))
        );
  }

  public updateEscalatedTicket(ticket: EscalatedTicket,
                               ticketUpdateBody: EscalatedTicketUpdateBody,
                               references: TicketReferenceData): Observable<TicketAPIResultFull<EscalatedTicket>> {
    return this.ticketsApiClient.updateEscalatedTicket(ticket, ticketUpdateBody)
        .pipe(map(apiResult => this.handleTicketApiResultFull(apiResult, references)));
  }

  // here ticketPatchBody uses Partial<Ticket> rather than Partial<T> is to avoid typescript compiler error (intended typescript behaviour)
  // refer to https://github.com/microsoft/TypeScript/issues/13442 for more details
  private patchTicket<T extends Ticket>(ticket: T, ticketPatchBody: Partial<TicketBody>, references: TicketReferenceData): Observable<TicketAPIResultFull<T>> {
    return this.ticketsApiClient.patchTicket(ticket, ticketPatchBody)
        .pipe(map(apiResult => this.handleTicketApiResultPartial(ticket, apiResult, references)));
  }

  public setTicketArchived<T extends Ticket>(ticket: T, ticketArchived: boolean, references: TicketReferenceData): Observable<TicketAPIResultFull<T>> {
    return this.patchTicket(ticket, { ticket_archived: ticketArchived }, references);
  }

  public assignEscalatedTicket(ticket: EscalatedTicket, assigneeId: string | null, references: TicketReferenceData): Observable<TicketAPIResultFull<EscalatedTicket>> {
    return this.patchTicket(ticket, { assignee_ids: assigneeId ? [assigneeId] : null }, references);
  }

  public linkAlertToAction(alert: AlertTicket, action: EscalatedTicket | null, references: TicketReferenceData): Observable<TicketAPIResultFull<AlertTicket>> {
    const patchBody = {
      target_ticket_id: action ? action.ticket_id : null
    };
    return this.patchTicket(alert, patchBody, references);
  }

  public linkActionToAlerts(action: EscalatedTicket, alerts: AlertTicket[] | null, references: TicketReferenceData): Observable<TicketAPIResultFull<EscalatedTicket>> {
    const patchBody = {
      source_ticket_ids: alerts?.map(alert => alert.ticket_id),
    };
    return this.patchTicket(action, patchBody, references);
  }

  public updateTicketInbox<T extends Ticket>(ticket: T, inbox: boolean, references: TicketReferenceData): Observable<TicketAPIResultFull<T>> {
    return this.patchTicket(ticket, { inbox }, references);
  }

  public updateTicketStatus<T extends Ticket>(ticket: T, statusId: TicketStatusId, references: TicketReferenceData): Observable<TicketAPIResultFull<T>> {
    return this.patchTicket(ticket, { status_id: statusId }, references);
  }

  public deleteTicket(ticketId: string): Observable<void> {
    return this.ticketsApiClient.deleteTicket(ticketId);
  }

  public upsertActionTimeTaken(action: EscalatedTicket, timeTaken: Duration | null, references: TicketReferenceData): Observable<TicketAPIResultFull<EscalatedTicket>> {
    const patchBody = {
      time_taken: timeTaken
    };
    return this.patchTicket(action, patchBody, references);
  }

  // endregion

  // region // ticket comment REST Create/Update
  createTicketComment(comment: TicketCommentCreationFields, includeTicketUpdateFields?: boolean): Observable<TicketCommentResult> {
    return this.ticketsApiClient.createTicketComment(comment, includeTicketUpdateFields, !this.userProfileService.getLoggedInUser());
  }

  updateTicketComment(comment: TicketCommentEditFields, includeTicketUpdateFields?: boolean): Observable<TicketCommentResult> {
    return this.ticketsApiClient.updateTicketComment(comment, includeTicketUpdateFields);
  }

  // endregion

  getAttachmentUploadUrls(fileNames: string[]): Observable<AttachmentUploadUrl[]> {
    return this.ticketsApiClient.getAttachmentUploadUrls(fileNames);
  }

  uploadFile(url: string, file: File, progressCallback: (filename: string, progress: number) => void, completeCallback: (filename: string) => void): Observable<any> {
    return this.ticketsApiClient.uploadFile(url, file, progressCallback, completeCallback);
  }

  deleteTicketAttachment(ticketId: string, attachmentId: string): Observable<any> {
    return this.ticketsApiClient.deleteTicketAttachment(ticketId, attachmentId);
  }

  associateAttachmentsWithTicket(ticketId: string, attachmentUrls: AttachmentUploadUrl[]): Observable<TicketAttachment[]> {
    return this.ticketsApiClient.associateAttachmentsWithTicket(ticketId, attachmentUrls);
  }

  prepareEmail(ticket: EscalatedTicket, cc: string[], campaignName: EmailTrackingCampaignName, equipmentById: Map<number, Equipment>): TicketEmail {
    const config = this.configService.getCachedConfig().tickets;
    const emailConfig = config && config.email;
    const ccEmails = [...(emailConfig && emailConfig.cc) ? emailConfig.cc : [], ...(cc ? cc : [])];
    const issue = ticket.description ? this.translateService.instant('Issue') + ':\n' + ticket.description + '\n' : '';
    const equipment = ticket.equipment_ids?.length
      ? (
        '\n' + this.translateService.instant('Equipment') + ':\n'
        + ticket.equipment_ids.map(equipmentId => equipmentById.get(equipmentId)?.name || equipmentId).join(', ') + '\n'
      )
      : '';
    const solution = ticket.solution ? '\n' + this.translateService.instant('Action Required') + ':\n' + ticket.solution + '\n' : '';
    const ticketBenefitLinkParam = (ticket.benefit && ticket.benefit.length
        ? '&benefit.benefit_name=' + encodeURIComponent(ticket.benefit[0].benefit_name)
        : '');
    const link = '\n' + this.translateService.instant('Tap link below when actioned') + ':\n'
        + `${window.location.origin}/public/tickets/${ticket.ticket_id}/comment`
        + `?site_id=${ticket.site_id}`
        + `&summary=` + encodeURIComponent(ticket.summary)
        + '&' + encodeURIComponent(emailTrackingCampaignQueryParamName) + '=' + encodeURIComponent(campaignName)
        + ticketBenefitLinkParam;

    return {
      to: ticket.assignees && ticket.assignees.length ? [ticket.assignees[0].email] : null,
      cc: ccEmails && ccEmails.length ? ccEmails : null,
      subject: (ticket.site ? ticket.site.site_name + ' - ' : '') + ticket.summary,
      body: issue + equipment + solution + link,
      'content-type': TicketEmailContentType.TEXT_PLAIN
    };
  }

  prepareSimpleAssignEmail(ticket: EscalatedTicket, user: User, assignees: User[]): TicketEmail {
    const subjectMessage: I18nMessage = {messageKey: 'User assigned you a new action at site', parameters: {user: user.fullName, site: ticket.site.site_name}};
    let bodyText = this.translateService.instant('Action') + ': ' + ticket.summary;
    bodyText += '\n\n' + this.translateService.instant('Description') + ':\n' + ticket.description;
    const ticketUrl = new URL(TicketUtils.makeEscalatedTicketUrl(ticket.site_id, ticket.ticket_id));
    ticketUrl.searchParams.append(emailTrackingCampaignQueryParamName, 'assign_email');
    bodyText += '\n\n' + this.translateService.instant('View Action or Reply') + ':\n' + ticketUrl.toString();
    return {
      to: assignees.map(assignee => assignee.email),
      subject: this.translateService.instant(subjectMessage),
      body: bodyText,
      'content-type': TicketEmailContentType.TEXT_PLAIN
    };
  }

  sendEmail(email: TicketEmail, ticketId: string): Observable<any> {
    return this.ticketsApiClient.sendEmail(email, ticketId);
  }

  getTicketTypeName(type: TicketType, plural: boolean = true): I18nMessage {
    switch (type) {
      case TicketType.ESCALATED:
        return { messageKey: plural ? 'Actions' : 'Action' };
      case TicketType.ALERT:
        return { messageKey: plural ? 'Alerts' : 'Alert' };
      default:
        throw new Error('Unknown Ticket Type "' + type + '"');
    }
  }

  public getBadgeColorForPriority(priorityId: number): string {
    const color = this.colorByPriority.get(priorityId);
    return 'text-bg-' + (color || this.DEFAULT_COLOR);
  }

  public getChangeHistoryForEscalatedTicket(ticketId: string,
                                            start_index: number,
                                            limit: number,
                                            sort_order?: SortOrder): Observable<{ count: number, change_history: TicketChangeHistory[] }> {
    return this.ticketsApiClient.getChangeHistoryForEscalatedTicket(ticketId, start_index, limit, sort_order);
  }

  private shouldChangeHistoryFieldBeDisplayed(changeHistory: TicketChangeHistory, field: TicketChangeHistoryField, fieldHideList: Set<TicketFieldId>): boolean {
    const canDisplayField = !fieldHideList.has(field?.field_id);
    if (canDisplayField && field.field_id === TicketFieldId.LAST_FAULT_TRANSITIONED_AT) {
      const lastFaultTransitionedAtStr = field.new_value['value'];
      const lastFaultTransitionedAt = moment.utc(lastFaultTransitionedAtStr);
      return Math.abs(changeHistory.ts.diff(lastFaultTransitionedAt)) >= this.DISPLAY_LAST_FAULT_TRANSITIONED_AT_DIFF_THRESHOLD;
    }
    return canDisplayField;
  }

  public transformToViewableChangeHistoryRecord(ticketType: TicketType, changeHistoryRecord: TicketChangeHistory): TicketChangeHistory {
    if (changeHistoryRecord) {
      const fieldById = new Map<TicketFieldId, TicketChangeHistoryField>();
      const fieldHideList = this.CHANGE_HISTORY_FIELDS_NOT_TO_DISPLAY_BY_TICKET_TYPE.get(ticketType);
      changeHistoryRecord.fields?.forEach(field => {
        if (this.shouldChangeHistoryFieldBeDisplayed(changeHistoryRecord, field, fieldHideList)) {
          fieldById.set(field.field_id, field);
        }
      });
      // If the resolved_at timestamp was set by the status change, rather than manually, then do not display it
      if (fieldById.has(TicketFieldId.RESOLVED_AT) && fieldById.has(TicketFieldId.STATUS_ID)) {
        const resolvedAtChangeHistory = fieldById.get(TicketFieldId.RESOLVED_AT);
        const resolvedTs = resolvedAtChangeHistory.new_value['value'] ? moment.utc(resolvedAtChangeHistory.new_value['value']) : null;
        if (!resolvedTs || resolvedTs.isSame(changeHistoryRecord.ts)) {
          fieldById.delete(TicketFieldId.RESOLVED_AT);
        }
      }
      // If change history record does not have any fields to display return null
      return fieldById.size ? { ...changeHistoryRecord, fields: Array.from(fieldById.values()) } : null;
    } else {
      return changeHistoryRecord;
    }
  }

  getTicketAggregation(aggregationParams: TicketAggregationParams, references?: boolean):
      Observable<{ ticket_aggregation: { [key: string]: any }, references?: { [key: string]: any } | null }> {
    return this.ticketsApiClient.getTicketAggregation(aggregationParams, references);
  }

  getBulkTicketAggregation(params: BulkTicketAggregationParams): Observable<{ticket_aggregations: {[key: string]: any}[]}> {
    return this.ticketsApiClient.getBulkTicketAggregation(params);
  }

  public resetTicketStore(): void {
    this.ticketsStore.reset();
  }

  public getChartLinkParamsForAlertTask(task: Task, site: SiteInfo, lastFaultTs: moment.Moment): Params {
    return this.chartService.getChartLinkQueryParams(task, {
      startDate: lastFaultTs.clone().subtract(this.ALERT_CHART_LINK_BACK_TRACK_DAYS, 'days'),
      endDate: lastFaultTs.clone().add(this.ALERT_CHART_LINK_FORWARD_TRACK_DAYS, 'days'),
      split: true,
      splitByUnit: true,
      workingHours: ScheduleUtil.isWorkingHoursAvailableOnChart(site.working_hours)
    });
  }
}
